import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('string-array-input')
export class StringArrayInput extends LitElement {
  // We use @property so the parent can pass in an initial array
  // and so Lit reacts when we update this.values internally.
  @property({ type: Array, attribute: true })
  public values: string[] = [];

  @property({ type: String, reflect: true })
  public inputType: string = 'text';

  static styles = css`
    :host {
      display: block;
    }
    .input-row {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      align-items: center;
    }
    input {
      padding: 5px;
      width: calc(100% - 18px);
      border-radius: 5px;
      font-size: 16px;
    }
    button {
      cursor: pointer;
      padding: 8px 12px;
      border: none;
      border-radius: 4px;
      background-color: #eee;
      transition: background 0.2s;
    }
    button:hover {
      background-color: #ddd;
    }
    button.remove-btn {
      background-color: #ffebee;
      color: #c62828;
    }
    button.remove-btn:hover {
      background-color: #ffcdd2;
    }
    button.add-btn {
      background-color: var(--primary-color);
      color: var(--background-color);
      width: 100%;
      margin-top: 4px;
    }
    button.add-btn:hover {
      background-color: var(--primary-hover-color);
    }
  `;

  /**
   * Dispatches a custom event so the parent component can retrieve the
   * updated array.
   */
  private _dispatchChange() {
    const options = {
      detail: { values: this.values },
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent('values-changed', options));
  }

  /**
   * Updates a specific index in the array when a user types.
   */
  private _handleInput(e: Event, index: number) {
    const input = e.target as HTMLInputElement;

    // Create a shallow copy of the array to ensure Lit reactivity triggers
    const newValues = [...this.values];
    newValues[index] = input.value;

    this.values = newValues;
    this._dispatchChange();
  }

  /**
   * Adds a new empty string to the array.
   */
  private _handleAdd() {
    this.values = [...this.values, ''];
    this._dispatchChange();

    // Optional: Focus the new input after render
    // (requires updateComplete to wait for DOM to exist)
    this.updateComplete.then(() => {
      const inputs = this.shadowRoot?.querySelectorAll('input');
      if (inputs && inputs.length > 0) {
        inputs[inputs.length - 1].focus();
      }
    });
  }

  /**
   * Removes an item at a specific index.
   */
  private _handleRemove(index: number) {
    const newValues = [...this.values];
    newValues.splice(index, 1);

    this.values = newValues;
    this._dispatchChange();
  }

  render(): TemplateResult {
    return html`
      <div class="container">
        <!-- Loop through the values array -->
        ${this.values.map(
          (val, index) => html`
            <div class="input-row">
              <input
                type="${this.inputType}"
                .value="${val}"
                @input="${(e: Event) => this._handleInput(e, index)}"
                placeholder="Enter value..."
              />
              <button
                class="remove-btn"
                @click="${() => this._handleRemove(index)}"
                title="Remove item"
              >
                &times;
              </button>
            </div>
          `,
        )}
        <langhout-button
          class="add-btn"
          @click="${this._handleAdd}"
          text="+ Add New Item"
        ></langhout-button>
      </div>
    `;
  }
}
